import type { ChatMessage, ImageRecord, VideoRecord } from "./types";

class HttpError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, { credentials: "same-origin", ...init });
  if (res.status === 401) throw new HttpError(401, "Unauthorized");
  if (!res.ok) {
    const txt = await res.text().catch(() => "");
    throw new HttpError(res.status, txt || res.statusText);
  }
  return (await res.json()) as T;
}

export const api = {
  async login(password: string): Promise<void> {
    await call("/api/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ password }),
    });
  },
  async logout(): Promise<void> {
    await call("/api/logout", { method: "POST" });
  },
  async session(): Promise<boolean> {
    try {
      await call("/api/session");
      return true;
    } catch {
      return false;
    }
  },
  async generateImage(prompt: string, opts: { aspect_ratio?: string; n?: number } = {}): Promise<ImageRecord[]> {
    const { images } = await call<{ images: ImageRecord[] }>("/api/image", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ prompt, ...opts }),
    });
    return images;
  },
  async submitVideo(
    body:
      | { kind: "text"; prompt: string; duration?: number; aspect_ratio?: string }
      | { kind: "image"; prompt: string; source_image_id: string; duration?: number; aspect_ratio?: string }
      | { kind: "extend"; prompt: string; source_video_id: string; duration?: number },
  ): Promise<{ id: string }> {
    return await call<{ id: string }>("/api/video", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  },
  async getVideo(id: string): Promise<VideoRecord> {
    return await call<VideoRecord>(`/api/video/${encodeURIComponent(id)}`);
  },
  async history(): Promise<{ images: ImageRecord[]; videos: VideoRecord[] }> {
    return await call<{ images: ImageRecord[]; videos: VideoRecord[] }>("/api/history");
  },
  async chatStream(messages: ChatMessage[], onDelta: (chunk: string) => void, signal?: AbortSignal): Promise<string> {
    const res = await fetch("/api/chat", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ messages: messages.map((m) => ({ role: m.role, content: m.content })) }),
      signal,
    });
    if (res.status === 401) throw new HttpError(401, "Unauthorized");
    if (!res.ok || !res.body) throw new HttpError(res.status, await res.text().catch(() => res.statusText));

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buf = "";
    let full = "";

    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      const lines = buf.split("\n");
      buf = lines.pop() ?? "";
      for (const line of lines) {
        if (!line.startsWith("data:")) continue;
        const payload = line.slice(5).trim();
        if (!payload || payload === "[DONE]") continue;
        try {
          const parsed = JSON.parse(payload);
          const delta: string | undefined = parsed?.choices?.[0]?.delta?.content;
          if (delta) {
            full += delta;
            onDelta(delta);
          }
        } catch {
          // ignore malformed lines
        }
      }
    }
    return full;
  },
};

export { HttpError };
